import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  Stack,
  Container,
  Tooltip
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import Navbar from "./Navbar";
import { useAuth } from './AuthContext';

import HeroImage from "./images/stamps.png";
import PocBadge from "./images/poc.png";

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />

      {/* Background image */}
      <Image
        src={HeroImage}
        alt="Hero Watermark"
        width="100%"
        height="100%"
        objectFit="cover"
        opacity={0.30}
        position="absolute"
        top={0}
        left={0}
        zIndex={-1}
        filter="blur(1px)"
      />

      {!user ? (
        <Container maxW="container.md" py={20}>
          <Box
            bg="white"
            p={6}
            borderRadius="md"
            boxShadow="md"
            textAlign="center"
          >
            <Heading size="lg" mb={4}>
              Welcome to mrc.ai
            </Heading>
            <Text fontSize="md">
              You must be registered and logged in with LinkedIn to view this content.
            </Text>
          </Box>
        </Container>
      ) : (
        <Container maxW="container.xl" py={8}>
          <Stack spacing={6} align="left" textAlign="center">
            <Box
              color="blue.600"
              bg="rgba(255,255,255,0.9)"
              p={4}
              borderRadius="md"
              boxShadow="lg"
              border="1px solid #3182ce"
              textAlign="left"
            >
              <Heading size="lg" mb={2}>Proof of Concept</Heading>
              <Flex
                direction={{ base: "column", md: "row" }}
                align="flex-start"
                gap={6}
              >
                <Box flex="1">
                  <Text fontSize="lg" fontWeight="bold" mb={2}>
                    Explore the mrc.ai PoC
                  </Text>
                  <Text fontSize="md" lineHeight="1.5" color="gray.700">
                    This <Text as="span" fontWeight="semibold" color="blue.600">Proof of Concept</Text> demonstrates how a <Text as="span" fontWeight="semibold">smart contracting platform</Text> can <Text as="span" fontWeight="semibold">centralize</Text> co-authored contracts for <Text as="span" fontWeight="semibold">standardized (Re)insurance Products</Text> — from <Text as="span" fontWeight="semibold">Submission</Text> through <Text as="span" fontWeight="semibold">Bind</Text> and into full <Text as="span" fontWeight="semibold">policy lifecycle management</Text> — within a single, unified interface supporting multiple Products and participants.
                  </Text>
                  <br />
                </Box>

                <Box
                  flexShrink={0}
                  top="20px"
                  right="20px"
                  width={{ base: "120px", md: "160px" }}
                  height="auto"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="transparent"
                  borderRadius="md"
                  boxShadow="md"
                  _hover={{ transform: "scale(1.05)" }}
                  transition="transform 0.2s ease-in-out"
                  cursor="pointer"
                  onClick={handleClick}
                >
                  <Image
                    src={PocBadge}
                    alt="Click to enter PoC"
                    boxSize={{ base: "120px", md: "160px" }}
                    objectFit="contain"
                  />
                </Box>

                
              </Flex>
            </Box>

            {/* Workflow Section */}
            <Box
              bg="rgba(255, 255, 255, 0.9)"
              p={4}
              borderRadius="md"
              boxShadow="md"
              border="1px solid #3182ce"
            >
              <Text fontSize="lg" fontWeight="bold" mb={2}>Workflow</Text>
              <br />
              {[
                { name: "Temporal", url: "http://217.154.50.62:8088/", desc: "View Temporal workflow instance." },


              ].map(({ name, url, desc }) => (
                <Box key={name} mb={4}>
                  <Text
                    as="a"
                    href={url}
                    color="blue.600"
                    fontSize="lg"
                    target="_blank"
                    rel="noopener noreferrer"
                    display="block"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {name}
                  </Text>
                  <Text fontSize="sm" fontStyle="italic">{desc}</Text>
                </Box>
              ))}
            </Box>






            {/* API Swaggers Section */}
            <Box
              bg="rgba(255, 255, 255, 0.9)"
              p={4}
              borderRadius="md"
              boxShadow="md"
              border="1px solid #3182ce"
            >
              <Text fontSize="lg" fontWeight="bold" mb={2}>API Swaggers</Text>
              <br />
              {[
                { name: "AuthAPI", url: "https://auth.poc.mrc.ai/swagger", desc: "Manages Users and LinkedIn Authentication." },
                { name: "ClockAPI", url: "https://clock.poc.mrc.ai/swagger", desc: "Handles real-time and simulated time controls..." },
                { name: "CodexAPI", url: "https://codex.mrc.ai/swagger", desc: "Provides platform level master data management services..." },
                { name: "CodifyAPI", url: "https://codify.poc.mrc.ai/swagger", desc: "Provides codify services..." },
                { name: "DirectoryAPI", url: "https://directory.poc.mrc.ai/swagger", desc: "Manages parties..." },
                { name: "DocumentsAPI", url: "https://documents.poc.mrc.ai/swagger", desc: "Manages document definitions and documents..." },
                { name: "LedgersAPI", url: "https://ledgers.poc.mrc.ai/swagger", desc: "Smart Ledger services..." },
                { name: "MrcaiAPI", url: "https://poc.mrc.ai/swagger", desc: "Central application services..." },
                { name: "PannerAPI", url: "https://panner.poc.mrc.ai/swagger", desc: "Provides Panner services..." },
                { name: "PipesAPI", url: "https://pipes.mrc.ai/swagger", desc: "Background pipework services..." },
                { name: "RegistersAPI", url: "https://registers.poc.mrc.ai/swagger", desc: "Manages registers..." },
                { name: "WorkflowAPI", url: "https://workflow.poc.mrc.ai/swagger", desc: "Manages workflow processes..." },
                { name: "XolAPI", url: "https://poc.mrc.ai/xol/swagger", desc: "Manages Excess of Loss contracts..." },
                
                
              ].map(({ name, url, desc }) => (
                <Box key={name} mb={4}>
                  <Text
                    as="a"
                    href={url}
                    color="blue.600"
                    fontSize="lg"
                    target="_blank"
                    rel="noopener noreferrer"
                    display="block"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {name}
                  </Text>
                  <Text fontSize="sm" fontStyle="italic">{desc}</Text>
                </Box>
              ))}
            </Box>
          </Stack>
        </Container>
      )}
    </Box>
  );
};

export default Home;
